import * as XLSX from 'xlsx';
import { api } from '../api/apiClient';

export const PENTEST_MAX_FILE_BYTES = 5 * 1024 * 1024;
export const PENTEST_MAX_TEXT_CHARS = 40000;
export const PENTEST_MIN_TEXT_CHARS = 20;
export const PENTEST_MAX_FILES = 5;
const ACCEPTED_EXTENSIONS = ['.txt', '.md', '.csv', '.pdf'];

export const getFileExtension = (filename = '') => {
  const lower = String(filename).toLowerCase();
  const idx = lower.lastIndexOf('.');
  return idx >= 0 ? lower.slice(idx) : '';
};

export const isAcceptedPentestFile = (file) => {
  if (!file) return false;
  if (file.size > PENTEST_MAX_FILE_BYTES) return false;
  return ACCEPTED_EXTENSIONS.includes(getFileExtension(file.name));
};

const readAsText = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(new Error('read_error'));
  reader.readAsText(file);
});

const readAsArrayBuffer = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('read_error'));
  reader.readAsArrayBuffer(file);
});

const extractPdfText = async (file) => {
  const buffer = await readAsArrayBuffer(file);
  const pdfjs = await import('pdfjs-dist');
  // ponytail: worker served from public/ (copied from pdfjs-dist build) — CRA has no worker-loader
  pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;
  const loadingTask = pdfjs.getDocument({ data: buffer });
  const pdf = await loadingTask.promise;
  const maxPages = Math.min(pdf.numPages, 60);
  const chunks = [];
  for (let i = 1; i <= maxPages; i += 1) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const strings = (content.items || []).map((it) => it.str || '').filter(Boolean);
    if (strings.length > 0) chunks.push(strings.join(' '));
  }
  try {
    if (typeof pdf.destroy === 'function') await pdf.destroy();
    else if (typeof pdf.cleanup === 'function') await pdf.cleanup();
  } catch { /* ignore cleanup errors */ }
  return chunks.join('\n\n');
};

const extractCsvText = async (file) => {
  const buffer = await readAsArrayBuffer(file);
  const workbook = XLSX.read(buffer, { type: 'array' });
  const firstSheet = workbook.SheetNames[0];
  if (!firstSheet) return '';
  const rows = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheet], { header: 1, defval: '' });
  return rows
    .filter((row) => Array.isArray(row) && row.some((c) => String(c).trim() !== ''))
    .map((row) => row.map((c) => String(c).trim()).join(' | '))
    .join('\n');
};

/**
 * Extract raw text from a pentest report file (txt/md/csv/pdf).
 * @throws {Error} with code message: too_big | bad_type | scanned_pdf | read_error | empty
 */
export const extractTextFromPentestFile = async (file) => {
  if (!file) throw new Error('bad_type');
  if (file.size > PENTEST_MAX_FILE_BYTES) throw new Error('too_big');
  const ext = getFileExtension(file.name);
  if (!ACCEPTED_EXTENSIONS.includes(ext)) throw new Error('bad_type');
  let text = '';
  if (ext === '.pdf') {
    text = await extractPdfText(file);
    if (text.trim().length < 200) throw new Error('scanned_pdf');
  } else if (ext === '.csv') {
    text = await extractCsvText(file);
  } else {
    text = await readAsText(file);
  }
  const cleaned = String(text || '').trim();
  if (!cleaned) throw new Error('empty');
  return cleaned.slice(0, PENTEST_MAX_TEXT_CHARS);
};

/**
 * Normalize pasted report text (textarea input).
 * @throws {Error} with code message: empty | too_short
 */
export const normalizePastedText = (text) => {
  const cleaned = String(text || '').trim();
  if (!cleaned) throw new Error('empty');
  if (cleaned.length < PENTEST_MIN_TEXT_CHARS) throw new Error('too_short');
  return cleaned.slice(0, PENTEST_MAX_TEXT_CHARS);
};

/**
 * Combine per-file texts into one prompt body capped at maxChars.
 * Each file gets a `===== FILE: name =====` separator so the model
 * can attribute findings. Overflow sets `truncated`.
 * @param {Array<{filename: string, text: string}>} parts
 * @returns {{ rawText: string, truncated: boolean, included: string[] }}
 */
export const combineReportTexts = (parts, maxChars = PENTEST_MAX_TEXT_CHARS) => {
  const chunks = [];
  const included = [];
  let used = 0;
  let truncated = false;
  for (const part of parts || []) {
    const header = `\n\n===== FILE: ${part.filename} =====\n\n`;
    const room = maxChars - used;
    if (room <= header.length + 100) { truncated = true; break; }
    const text = String(part.text || '');
    const slice = text.slice(0, room - header.length);
    if (slice.length < text.length) truncated = true;
    chunks.push(header + slice);
    used += header.length + slice.length;
    included.push(part.filename);
  }
  if ((parts || []).length > included.length) truncated = true;
  return { rawText: chunks.join('').trim(), truncated, included };
};

/**
 * Call backend AI extraction. Returns { findings, truncated, filename }.
 */
export const requestPentestExtraction = async ({ analysisId, filename, rawText, businessAssets }) => {
  const response = await api.post('/ai/extract-pentest-findings', {
    analysisId,
    filename,
    rawText,
    businessAssets: (businessAssets || []).map((a) => ({
      id: String(a.id),
      name: a.name,
      vendor: a.vendor,
      product: a.product,
      version: a.version,
      type: a.type || a.nature,
    })),
  });
  if (!response?.success) {
    throw new Error(response?.message || 'extraction_failed');
  }
  return response.data;
};

export const suggestionToStatus = (suggestion) => {
  if (suggestion === 'corrige') return 'corrige';
  return 'non_corrige';
};

/**
 * Linked selection: toggling a finding propagates to its linked suggested techniques
 * (matched by relatedFindingTempId). Manual technique toggles stay independent otherwise.
 * Pure function.
 * @returns {{ items, techItems }} patched arrays
 */
export const propagateFindingToggle = (items, techItems, tempId, enabled) => ({
  items: (items || []).map((it) => (it.tempId === tempId ? { ...it, enabled } : it)),
  techItems: (techItems || []).map((it) => (
    it.relatedFindingTempId && it.relatedFindingTempId === tempId ? { ...it, enabled } : it
  )),
});

/**
 * Map a confirmed finding to the CTI vulnerability shape used by CTIDisplaySimple.
 */
export const findingToVulnerability = (finding, { asset, attackPath, filename, origin }) => {
  const id = finding.cveId || finding.tempId || `PENTEST-${Date.now()}`;
  const severityLevel = finding.severityHint || 'UNKNOWN';
  const itemOrigin = origin || finding.origin || 'pentest';
  return {
    id,
    cveId: finding.cveId || null,
    title: finding.title,
    description: finding.remediation
      ? `${finding.description}\n\nRemédiation (rapport): ${finding.remediation}`
      : finding.description,
    publishedDate: null,
    lastModified: null,
    references: [],
    severity: { severity: severityLevel, severityLevel, score: null },
    score: null,
    cvssScore: null,
    epss: null,
    kev: false,
    euvdExploited: false,
    status: finding.confirmedStatus || suggestionToStatus(finding.statusSuggestion),
    assetId: asset.id,
    assetName: asset.name,
    attackPathId: attackPath?.id || asset.id,
    attackPathName: attackPath?.referenceCode || attackPath?.objectifVise || attackPath?.name,
    businessValueName: attackPath?.businessValueName,
    _origin: itemOrigin,
    _enriched: false,
    _pentestFile: filename || null,
    _pentestEvidence: finding.statusEvidence || null,
    _pentestTitle: finding.title,
  };
};

// ============================================================================
// Asset-level pool (persisted, path-independent)
// ============================================================================

export const fetchPentestPool = async (analysisId) => {
  const response = await api.get(`/analyses/${analysisId}/pentest-findings`);
  if (!response?.success) throw new Error(response?.message || 'pool_load_failed');
  return response.data?.findings || [];
};

export const savePentestPoolBatch = async (analysisId, findings) => {
  const response = await api.post(`/analyses/${analysisId}/pentest-findings`, { findings });
  if (!response?.success) throw new Error(response?.message || 'pool_save_failed');
  return response.data;
};

export const deletePentestPoolFinding = async (analysisId, id) => {
  const response = await api.delete(`/analyses/${analysisId}/pentest-findings/${id}`);
  if (!response?.success) throw new Error(response?.message || 'pool_delete_failed');
  return response.data;
};

export const poolDocId = (doc) => doc.cveId || String(doc._id || doc.tempId);

/** A pool doc is active unless explicitly pending (missing field = confirmed, backward compat). */
export const isPoolConfirmed = (doc) => doc?.state !== 'pending';

export const splitPoolByState = (pool) => ({
  confirmed: (pool || []).filter(isPoolConfirmed),
  pending: (pool || []).filter((d) => !isPoolConfirmed(d)),
});

const isRealAssetId = (id) => id && !String(id).startsWith('pentest-asset-');

/**
 * Map persisted pool docs back to review-modal item shape (reopen/edit flow).
 * Preserves enabled state (confirmed → on, pending → off) and technique→finding
 * linkage via parentFindingId with title-match fallback. Pure function.
 * @returns {{ findings: Array, techniques: Array }}
 */
export const poolToReviewState = (pool) => {
  const docs = pool || [];
  const findings = docs
    .filter((d) => d.kind !== 'technique')
    .map((d) => ({
      tempId: String(d._id),
      _poolId: String(d._id),
      title: d.title,
      description: d.description,
      cveId: d.cveId || null,
      severityHint: d.severityHint || 'UNKNOWN',
      statusSuggestion: d.statusSuggestion || 'inconnu',
      statusEvidence: d.statusEvidence || '',
      remediation: d.remediation || '',
      suggestedAssetId: isRealAssetId(d.assetId) ? String(d.assetId) : null,
      suggestedAssetName: d.assetName || '',
      assetConfidence: 'high',
      confirmedStatus: d.statusSuggestion === 'corrige' ? 'corrige' : 'non_corrige',
      enabled: isPoolConfirmed(d),
    }));
  const findingTempByPoolId = new Map(findings.map((f) => [f.tempId, f.tempId]));
  const techniques = docs
    .filter((d) => d.kind === 'technique')
    .map((d) => {
      let parentTempId = (d.parentFindingId && findingTempByPoolId.get(String(d.parentFindingId))) || null;
      if (!parentTempId && d.relatedFindingTitle) {
        const match = findings.find((f) => f.title === d.relatedFindingTitle);
        parentTempId = match ? match.tempId : null;
      }
      return {
        tempId: String(d._id),
        _poolId: String(d._id),
        techniqueId: d.techniqueId || null,
        name: d.title,
        tactic: d.tactic || '',
        description: d.description || '',
        rationale: d.rationale || '',
        relatedFindingTitle: d.relatedFindingTitle || '',
        relatedFindingTempId: parentTempId,
        suggested: !!d.suggested,
        mitreVerified: d.mitreVerified === true,
        mitigations: Array.isArray(d.mitigations) ? d.mitigations : [],
        suggestedAssetId: isRealAssetId(d.assetId) ? String(d.assetId) : null,
        suggestedAssetName: d.assetName || '',
        assetConfidence: 'high',
        enabled: isPoolConfirmed(d),
      };
    });
  return { findings, techniques };
};

/** True for scan-report and network-diagram origins (classic CTI items carry no origin). */
export const isScanOrigin = (item) => {
  const origin = item?._origin || item?.origin;
  return origin === 'pentest' || origin === 'diagram';
};

/** Stable merge/tombstone key: CVE id or technique id, fallback to doc id. */
export const poolMergeKey = (doc) => {
  if (doc.kind === 'technique') return doc.techniqueId || String(doc._id || doc.tempId);
  return poolDocId(doc);
};

/**
 * Map a persisted pool doc to the CTI vulnerability shape.
 * attackPath may be null (pool preview) — path fields are filled on merge.
 */
export const poolDocToVulnerability = (doc, attackPath) => {
  const id = poolDocId(doc);
  const severityLevel = doc.severityHint || 'UNKNOWN';
  return {
    id,
    cveId: doc.cveId || null,    title: doc.title,
    description: doc.remediation
      ? `${doc.description}\n\nRemédiation (rapport): ${doc.remediation}`
      : doc.description,
    publishedDate: null,
    lastModified: null,
    references: [],
    severity: { severity: severityLevel, severityLevel, score: null },
    score: null,
    cvssScore: null,
    epss: null,
    kev: false,
    euvdExploited: false,
    status: suggestionToStatus(doc.statusSuggestion),
    assetId: String(doc.assetId),
    assetName: doc.assetName,
    attackPathId: attackPath?.id || null,
    attackPathName: attackPath?.referenceCode || attackPath?.objectifVise || attackPath?.name || null,
    businessValueName: attackPath?.businessValueName || null,
    _origin: doc.origin === 'diagram' ? 'diagram' : 'pentest',
    _enriched: false,
    _poolId: doc._id ? String(doc._id) : null,
    _pentestFile: doc.sourceFilename || null,
    _pentestEvidence: doc.statusEvidence || null,
    _pentestTitle: doc.title,
  };
};

/**
 * Map a persisted technique pool doc to the CTI technique shape.
 */
export const poolDocToTechnique = (doc, attackPath) => {
  const id = doc.techniqueId || String(doc._id || doc.tempId);
  return {
    id,
    techniqueId: doc.techniqueId || null,
    name: doc.title,
    techniqueName: doc.title,
    tactic: doc.tactic || '',
    description: doc.description || '',
    rationale: doc.rationale || null,
    suggested: !!doc.suggested,
    _mitreVerified: doc.mitreVerified === true,
    mitigations: Array.isArray(doc.mitigations) ? doc.mitigations : [],
    relatedFindingTitle: doc.relatedFindingTitle || null,
    url: doc.url || (doc.techniqueId ? `https://attack.mitre.org/techniques/${String(doc.techniqueId).replace('.', '/')}` : null),
    assetId: String(doc.assetId),
    assetName: doc.assetName,
    attackPathId: attackPath?.id || null,
    attackPathName: attackPath?.referenceCode || attackPath?.objectifVise || attackPath?.name || null,
    businessValueName: attackPath?.businessValueName || null,
    _origin: doc.origin === 'diagram' ? 'diagram' : 'pentest',
    _enriched: false,
    _poolId: doc._id ? String(doc._id) : null,
    _pentestFile: doc.sourceFilename || null,
    _pentestEvidence: doc.statusEvidence || null,
  };
};

/**
 * Merge pool findings concerning the given asset ids into CTI results.
 * Pure function — dedupes by vulnerability/technique id, recomputes totals.
 * Assets missing from results are added from `extraAssets` shells when available.
 * @returns {{ results, added, addedTechniques, newStatuses }}
 */
export const mergePoolIntoResults = (prevResults, pool, assetIds, attackPath, extraAssets = []) => {
  const ids = new Set((assetIds || []).map((a) => String(a)));
  const existingIds = new Set();
  const collectTechId = (t) => t.id || t.techniqueId;
  (prevResults?.assets || []).forEach((a) => {
    (a.vulnerabilities || []).forEach((v) => existingIds.add(v.id));
    (a.attackTechniques || []).forEach((t) => existingIds.add(collectTechId(t)));
    (a.techniques || []).forEach((t) => existingIds.add(collectTechId(t)));
  });
  (prevResults?.vulnerabilities || []).forEach((v) => existingIds.add(v.id));
  (prevResults?.techniques || []).forEach((t) => existingIds.add(collectTechId(t)));
  (prevResults?.attackTechniques || []).forEach((t) => existingIds.add(collectTechId(t)));

  const shellsById = new Map((extraAssets || []).map((a) => [String(a.id), a]));
  const knownIds = new Set([
    ...(prevResults?.assets || []).map((a) => String(a.id)),
    ...shellsById.keys(),
  ]);

  const inScope = (pool || []).filter((doc) => ids.has(String(doc.assetId)));
  const candidates = inScope
    .filter((doc) => doc.kind !== 'technique')
    .map((doc) => poolDocToVulnerability(doc, attackPath))
    .filter((v) => !existingIds.has(v.id) && knownIds.has(String(v.assetId)));
  const techCandidates = inScope
    .filter((doc) => doc.kind === 'technique' && doc.techniqueId)
    .map((doc) => poolDocToTechnique(doc, attackPath))
    .filter((t) => !existingIds.has(t.id) && knownIds.has(String(t.assetId)));

  if (candidates.length === 0 && techCandidates.length === 0) {
    return { results: prevResults, added: [], addedTechniques: [], newStatuses: {} };
  }

  const byAsset = new Map();
  const pushToAsset = (item, isTech) => {
    const key = String(item.assetId);
    if (!byAsset.has(key)) byAsset.set(key, { vulns: [], techs: [] });
    byAsset.get(key)[isTech ? 'techs' : 'vulns'].push(item);
  };
  candidates.forEach((v) => pushToAsset(v, false));
  techCandidates.forEach((t) => pushToAsset(t, true));

  const mergedAssets = [...(prevResults?.assets || [])];
  byAsset.forEach(({ vulns, techs }, assetId) => {
    const idx = mergedAssets.findIndex((a) => String(a.id) === String(assetId));
    if (idx >= 0) {
      mergedAssets[idx] = {
        ...mergedAssets[idx],
        vulnerabilities: [...(mergedAssets[idx].vulnerabilities || []), ...vulns],
        attackTechniques: [...(mergedAssets[idx].attackTechniques || []), ...techs],
      };
    } else if (shellsById.has(String(assetId))) {
      const shell = shellsById.get(String(assetId));
      mergedAssets.push({
        ...shell,
        attackPathId: attackPath?.id || shell.attackPathId || shell.id,
        attackPathName: attackPath?.referenceCode || attackPath?.objectifVise || shell.attackPathName,
        businessValueName: attackPath?.businessValueName || shell.businessValueName,
        vulnerabilities: vulns,
        attackTechniques: [...(shell.attackTechniques || []), ...techs],
      });
    }
  });

  const allVulns = [...(prevResults?.vulnerabilities || []), ...candidates];
  const allTechs = [...(prevResults?.techniques || []), ...techCandidates];
  const newStatuses = {};
  candidates.forEach((v) => {
    newStatuses[v.id] = v.status;
  });

  return {
    results: {
      ...(prevResults || {}),
      assets: mergedAssets,
      vulnerabilities: allVulns,
      techniques: allTechs,
      attackTechniques: [...(prevResults?.attackTechniques || []), ...techCandidates],
      totalVulnerabilities: allVulns.length,
      totalAttackTechniques: allTechs.length,
      attackPath: attackPath || prevResults?.attackPath,
      analysisDate: prevResults?.analysisDate || new Date().toISOString(),
      dataSource: prevResults?.dataSource || 'Pentest',
    },
    added: candidates,
    addedTechniques: techCandidates,
    newStatuses,
  };
};
